// UGH!3D — main entry: scene boot, input, game loop, net wiring, taxi logic.
import {
  Engine, Scene, FreeCamera, Vector3, Color3, Color4, ParticleSystem,
  DynamicTexture, MeshBuilder, StandardMaterial, Mesh,
  ImageProcessingPostProcess, ColorCurves,
} from '@babylonjs/core';
import { buildWorld, buildFruitTemplates } from './world.js';
import {
  buildHeliMesh, Flight, applyHeliVisual, applyRemoteVisual, hoverRotor, GEAR_H,
} from './heli.js';
import { buildPteros, updatePteros, setPteroDown, startDive } from './pteros.js';
import { Rexes } from './rexes.js';
import { Weather } from './weather.js';
import { buildTouch, requestImmersive } from './touch.js';
import { Passengers } from './passengers.js';
import { buildHud } from './hud.js';
import { Net, RemoteBuffer } from './net.js';
import {
  initAudio, audioReady, setRotor, sfxUgh, sfxCoin, sfxEat, sfxCrash,
  sfxSplash, sfxSquawk, sfxThud, sfxRoar, sfxSnore,
  sfxThrow, sfxBonk, sfxPickupStone, setRainAudio, sfxThunder, sfxZap, sfxFanfare,
  sfxRustle, sfxTangle,
  setRoom, setWaterAudio, setListener, setEnergyState, setHullStress,
  resetSurvivalAudio, sfxLand, sfxFee, sfxGrowl, sfxScreech, sfxCharge, sfxHiss,
  sfxPaxMood, sfxIdleTick, remoteRotor, dropRemoteRotor, toggleMute,
} from './audio.js';
import {
  CAVES, HOME, FRUIT_SPOTS, WATER_Y, terrainHeight, REXES,
  STONE_SPOTS, pterosForLevel, ceilingY, GIANT_TREES, riverX,
  landScore, rexBlastRFor, boltFactorFor, BOLT_DAMAGE,
} from '../shared/level.mjs';

const canvas = document.getElementById('game');
const engine = new Engine(canvas, true, { stencil: false });
const scene = new Scene(engine);
scene.useRightHandedSystem = true;

const camera = new FreeCamera('cam', new Vector3(0, 40, -60), scene);
camera.minZ = 0.5; camera.maxZ = 2500;
camera.fov = 0.80;

// final color grade: a touch of contrast + saturation and a soft green
// vignette — the cheap trick that makes low-poly read as intentional style.
// main.js is the ONLY writer on exposure / vignetteWeight / vignetteColor.
const grade = new ImageProcessingPostProcess('grade', 1.0, camera);
grade.contrast = 1.12;
grade.exposure = 1.30;
grade.vignetteEnabled = true;
grade.vignetteWeight = 1.1;
grade.vignetteColor = new Color4(0.01, 0.05, 0.02, 0);
const VIG_CALM = new Color4(0.01, 0.05, 0.02, 0);
const VIG_HURT = new Color4(0.35, 0.02, 0.02, 0);
const curves = new ColorCurves();
curves.globalSaturation = 18;
grade.colorCurvesEnabled = true;
grade.colorCurves = curves;

// ---------------------------------------------------------------- input
const keys = {};
window.addEventListener('keydown', e => {
  if (e.code === 'Tab') e.preventDefault();
  keys[e.code] = true;
});
window.addEventListener('keyup', e => { keys[e.code] = false; });
const touch = buildTouch({ onThrow: () => throwStone() });
const clamp1 = v => Math.max(-1, Math.min(1, v));
// Keyboard is binary, so it gets ramped into something analog. The touch stick
// is ALREADY analog — ramping it a second time would make it feel like syrup.
let smThrottle = 0, smTurn = 0;
const input = (dt = 0.016) => {
  const rawT = clamp1(((keys.KeyW || keys.ArrowUp) ? 1 : 0) - ((keys.KeyS || keys.ArrowDown) ? 1 : 0) + touch.throttle);
  const rawR = clamp1(((keys.KeyA || keys.ArrowLeft) ? 1 : 0) - ((keys.KeyD || keys.ArrowRight) ? 1 : 0) + touch.turn);
  if (touch.active) { smThrottle = rawT; smTurn = rawR; }
  else {
    const k = Math.min(1, dt * 8);
    smThrottle += (rawT - smThrottle) * k;
    smTurn += (rawR - smTurn) * k;
  }
  return { pedal: !!(keys.Space || keys.KeyX || touch.pedal), throttle: smThrottle, turn: smTurn };
};

// ---------------------------------------------------------------- state
const hud = buildHud();
const net = new Net();
let myId = null, myName = '…', myCoins = 0;
let carrying = null;          // pax object while a passenger is on board
// Touchdown speeds of the LAST landing + the damage we had when the customer
// stepped in. All three are reset on respawn AND on board, so a previous trip
// can never pay for (or against) this one.
let lastVy = 99, lastVh = 99, dmgAtBoard = 0;
let respawnAt = 0;
let srvOffset = 0;            // serverTime(ms) - performance.now()
const srvNow = () => (performance.now() + srvOffset) / 1000;
const remotes = new Map();    // id -> {mesh, buf, name, prev}
const paxIndex = new Map();   // paxId -> pax data (server truth)
const roster = new Map();     // id -> {name, coins}

let world = null, heli = null, flight = null, pteros = null, passengers = null, rexes = null, weather = null;
const fruits = new Map();     // id -> {mesh, spot, gone}
let camShake = 0;             // trauma applied to the chase camera
let camSnap = true;           // snap (don't lerp) the camera on the next frame
let hurt = 0;                 // recent damage, drives the red vignette
let exposurePunch = 0;        // lightning flash
let prevDamage = 0;
let snoreAt = 0;              // next time we may play a nearby snore
let frame = 0;
const screechAt = new Map();  // ptero id -> next allowed screech (ms)
const slabPulse = new Map();  // caveId -> {mat, base, until}
const PULSE_COL = new Color3(1, 0.85, 0.25);
const stoneSpots = new Map(); // id -> {mesh, spot, gone}
let stoneCount = 0;           // 0-3 stones in the net under the seat
let carriedStone = null;      // mesh hanging under the heli
const flyingStones = [];      // {mesh, vel, born}
let stoneMatRef = null;
let levelInfo = { n: 1, delivered: 0, quota: 3 };
let streak = 0;
let warn30 = false, warn15 = false, reserveShown = false;
let rexToastAt = 0;
let landRing = null, landRingMat = null, landRingT = 0;
let downwash = null, downwashMat = null;

// ---------------------------------------------------------------- fx
// ONE dot texture for every burst in the game — never one per explosion.
let burstTex = null;
function burstTexture() {
  if (burstTex) return burstTex;
  const dt = new DynamicTexture('px', { width: 32, height: 32 }, scene, false);
  const c = dt.getContext();
  c.fillStyle = '#fff'; c.beginPath(); c.arc(16, 16, 14, 0, Math.PI * 2); c.fill();
  dt.update(); dt.hasAlpha = true;
  burstTex = dt;
  return dt;
}

function makeBurst(pos, color1, color2, n = 40) {
  const ps = new ParticleSystem('burst', n, scene);
  ps.particleTexture = burstTexture();
  ps.emitter = pos.clone();
  ps.minSize = 0.4; ps.maxSize = 1.4;
  ps.minLifeTime = 0.4; ps.maxLifeTime = 1.1;
  ps.emitRate = 1000;
  ps.direction1 = new Vector3(-6, 2, -6); ps.direction2 = new Vector3(6, 10, 6);
  ps.gravity = new Vector3(0, -14, 0);
  ps.color1 = color1; ps.color2 = color2;
  ps.targetStopDuration = 0.15;
  ps.disposeOnStop = true;
  ps.start();
}

// every jolt in the game goes through here — one channel, one decay curve
function shake(v) { camShake = Math.max(camShake, Math.min(1.2, v)); }

// a yellow heartbeat on the tower's own number slab, instead of a toast that
// buries the warnings you actually need to read
function pulseCave(id, ms = 2000) {
  const info = world && world.caveInfo.get(id);
  if (!info || !info.slabMat) return false;
  let s = slabPulse.get(id);
  if (!s) {
    s = { mat: info.slabMat, base: info.slabMat.emissiveColor.clone(), until: 0 };
    slabPulse.set(id, s);
  }
  s.until = performance.now() + ms;
  return true;
}

// ---------------------------------------------------------------- boot
const title = document.getElementById('title');
let started = false, booted = false;

async function boot() {
  world = await buildWorld(scene);
  heli = buildHeliMesh(scene, { hue: 25 });
  let lastRustle = 0, lastTangleToast = 0;
  flight = new Flight(world.colliders, {
    onFoliage() {
      const n = performance.now();
      if (n - lastRustle < 350) return;
      lastRustle = n;
      sfxRustle();
      shake(0.08);
      makeBurst(flight.pos, new Color4(0.25, 0.5, 0.25, 1), new Color4(0.4, 0.65, 0.3, 1), 10);
    },
    onTangle() {
      sfxTangle();
      shake(0.28);
      makeBurst(flight.pos, new Color4(0.3, 0.42, 0.16, 1), new Color4(0.24, 0.49, 0.24, 1), 16);
      const n = performance.now();
      if (n - lastTangleToast > 4000) {
        lastTangleToast = n;
        sfxHiss(0.5);
        hud.toast('🌿 En lian røg i rotoren!', 3000, 'fare');
      }
    },
    onLand(vy, vh, ground) {
      lastVy = vy; lastVh = vh;
      const q = landScore(vy, vh);
      sfxLand(q, vy);
      showLandRing(q);
      if (q > 0.9) hud.toast('🪶 Fjerlet landing!', 2200, 'good');
    },
    onThud(v) { sfxThud(v); shake(v * 1.3); },
    onCrash(reason, pos) {
      if (reason === 'splash') { sfxSplash(pos.x, pos.z); hud.big('💦 SPLASH! Motoren druknede'); }
      else { sfxCrash(pos.x, pos.z); hud.big('💥 KVAS! Helikopteren er smadret'); }
      shake(1.2);
      makeBurst(pos, new Color4(0.54, 0.35, 0.17, 1), new Color4(0.88, 0.31, 0.22, 1), 60);
      for (const m of heli.allMeshes) m.setEnabled(false);
      stoneCount = 0;
      if (carriedStone) carriedStone.setEnabled(false);
      if (carrying) {
        hud.toast('😵 Kunden forsvandt i kaosset…', 3200, 'fare');
        net.send({ t: 'crash', pax: carrying.id });
        passengers.remove(carrying.id);
        carrying = null;
      } else {
        net.send({ t: 'crash' });
      }
      respawnAt = performance.now() + 2600;
    },
  }, { crowns: world.crowns });
  for (const m of heli.allMeshes) world.shadows.addShadowCaster(m);
  pteros = buildPteros(scene);
  rexes = new Rexes(scene);
  passengers = new Passengers(scene);
  weather = new Weather(scene, { sun: world.sun, hemi: world.hemi }, {
    onFlash: s => { hud.flash(s); exposurePunch = Math.max(exposurePunch, 0.85 * s); shake(0.35 * s); },
    onThunder: (v, x, z) => sfxThunder(v, x, z),
    onStruck: (agl) => {
      if (flight.dead) return;
      // on a pad you are earthed and completely safe; the crown is a shelter
      const f = boltFactorFor(agl, !!flight.padUnder(), !!flight.inCrown);
      if (f <= 0) return;
      sfxZap();
      flight.damage += BOLT_DAMAGE * f;
      flight.vel.addInPlace(new Vector3((Math.random() - 0.5) * 12 * f, -10 * f, (Math.random() - 0.5) * 12 * f));
      flight.landed = false;
      shake(0.9 * f);
      hud.big(f >= 1.4
        ? '⚡ <b>FULDTRÆFFER!</b> Lynet fandt dig højt oppe!'
        : '⚡ <b>ZAP!</b> Lynet ramte helikopteren!', 2400);
    },
  });

  const tmpl = buildFruitTemplates(scene);
  for (const spot of FRUIT_SPOTS) {
    const mesh = tmpl[spot.type].clone('fruit' + spot.id);
    mesh.setEnabled(true);
    mesh.position.set(spot.x, spot.y, spot.z);
    fruits.set(spot.id, { mesh, spot, gone: false });
  }

  // stone piles + the stones carried under the heli
  const stoneMat = new StandardMaterial('stonePickupMat', scene);
  stoneMat.diffuseColor = Color3.FromHexString('#9b968c');
  stoneMat.emissiveColor = stoneMat.diffuseColor.scale(0.15);
  stoneMat.specularColor = Color3.Black();
  const pileParts = [];
  for (let i = 0; i < 4; i++) {
    const s = MeshBuilder.CreateSphere('sp' + i, { diameter: 1.5 - i * 0.2, segments: 3 }, scene);
    s.position.set([0.5, -0.5, 0.1, 0][i], [0.4, 0.4, 1.1, 0.4][i] * 0.6, [0, 0.3, 0.2, -0.5][i]);
    pileParts.push(s);
  }
  const pileTmpl = Mesh.MergeMeshes(pileParts, true);
  pileTmpl.material = stoneMat;
  pileTmpl.setEnabled(false);
  for (const spot of STONE_SPOTS) {
    const mesh = pileTmpl.clone('stonePile' + spot.id);
    mesh.setEnabled(true);
    mesh.position.set(spot.x, spot.y + 0.2, spot.z);
    stoneSpots.set(spot.id, { mesh, spot, gone: false });
  }
  carriedStone = MeshBuilder.CreateSphere('carriedStone', { diameter: 0.9, segments: 3 }, scene);
  carriedStone.material = stoneMat;
  carriedStone.parent = heli.tilt;
  carriedStone.position.set(0, 0.35, 0.1); // slung under the seat log
  carriedStone.setEnabled(false);
  stoneMatRef = stoneMat;

  buildGroundFx();

  engine.runRenderLoop(loop);
  window.addEventListener('resize', () => engine.resize());
  // a 'welcome' that lands before the world is built would spawn passengers
  // into a null Passengers — so the socket only ever opens from here
  booted = true;
  if (started) net.connect();
}

// ONE landing ring and ONE downwash disc for the whole session — both are
// built here at boot and only ever moved, scaled and faded afterwards.
function buildGroundFx() {
  landRing = MeshBuilder.CreateTorus('landRing', { diameter: 6, thickness: 0.28, tessellation: 28 }, scene);
  landRingMat = new StandardMaterial('landRingM', scene);
  landRingMat.diffuseColor = Color3.Black();
  landRingMat.emissiveColor = Color3.FromHexString('#8ce06a');
  landRingMat.specularColor = Color3.Black();
  landRingMat.disableLighting = true;
  landRingMat.alphaMode = 1;              // additive: never sorts wrong
  landRingMat.alpha = 0;
  landRing.material = landRingMat;
  landRing.isPickable = false;
  landRing.setEnabled(false);

  const dt = new DynamicTexture('downwashTex', { width: 64, height: 64 }, scene, false);
  const c = dt.getContext();
  const g = c.createRadialGradient(32, 32, 6, 32, 32, 31);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.62, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  c.clearRect(0, 0, 64, 64);
  c.fillStyle = g; c.beginPath(); c.arc(32, 32, 31, 0, Math.PI * 2); c.fill();
  dt.update(); dt.hasAlpha = true;
  downwash = MeshBuilder.CreateDisc('downwash', { radius: 5.5, tessellation: 20 }, scene);
  downwash.rotation.x = Math.PI / 2;
  downwashMat = new StandardMaterial('downwashM', scene);
  downwashMat.diffuseColor = Color3.Black();
  downwashMat.emissiveColor = Color3.FromHexString('#d8cf9e');
  downwashMat.specularColor = Color3.Black();
  downwashMat.disableLighting = true;
  downwashMat.opacityTexture = dt;
  downwashMat.alphaMode = 1;
  downwashMat.alpha = 0;
  downwash.material = downwashMat;
  downwash.isPickable = false;
  downwash.setEnabled(false);
}

function showLandRing(q) {
  if (!landRing) return;
  landRingT = 0.001;
  landRing.position.set(flight.pos.x, flight.pos.y - GEAR_H + 0.25, flight.pos.z);
  // green feather → amber → red slam, the same verdict the HUD shows
  landRingMat.emissiveColor.set(
    q > 0.55 ? 0.55 - 0.35 * q : 1,
    q > 0.25 ? 0.35 + 0.55 * q : 0.35 * (q / 0.25),
    0.25 + 0.2 * q,
  );
  landRing.setEnabled(true);
}

function start() {
  if (started) return;
  started = true;
  initAudio();
  let muted = false;
  try { muted = localStorage.getItem('ugh3d.muted') === '1'; } catch (e) { /* private mode */ }
  hud.setSound(!muted);
  if (touch.active) requestImmersive();
  title.classList.add('hide');
  setTimeout(() => title.remove(), 600);
  if (booted) net.connect();
}
title.addEventListener('click', start);
window.addEventListener('keydown', e => { if (e.code === 'Enter' || e.code === 'Space') start(); }, { once: false });

// sound toggle: the button lives inside #hud (pointer-events: none), so the
// button itself re-enables pointer events in index.html. blur() is critical —
// a focused button eats the next SPACE, and SPACE is the pedal.
const sndBtn = document.getElementById('snd');
if (sndBtn) {
  sndBtn.onmousedown = e => e.preventDefault();
  sndBtn.onclick = () => { initAudio(); hud.setSound(toggleMute()); sndBtn.blur(); };
}
window.addEventListener('keydown', e => {
  if (e.code === 'KeyM' && !e.repeat) { initAudio(); hud.setSound(toggleMute()); }
});

// ---------------------------------------------------------------- net
function attachPaxTo(paxId, playerId) {
  if (playerId === myId) {
    passengers.setBoarded(paxId, heli);
  } else {
    const r = remotes.get(playerId);
    if (r) passengers.setBoarded(paxId, r.mesh);
  }
}

// the server sends REMAINING patience (`left`), never an epoch — stamp it onto
// our own clock the moment we hear about the customer
function stampPax(px) {
  px.deadlineAt = srvNow() * 1000 + (px.left !== undefined ? px.left : (px.patience || 60000));
  if (!px.patience) px.patience = 60000;
  return px;
}

// A reconnect (every server restart is one) delivers a fresh world. Anything
// left over from the old connection has to go FIRST: orphan remote helicopters
// keep rendering and keep a looping rotor voice alive forever, and stale
// passenger ids collide with the server's restarted id counter — which makes
// Passengers.spawn() drop every new customer without a word.
function resetWorldState() {
  for (const [id, r] of remotes) { r.mesh.root.dispose(); dropRemoteRotor(id); }
  remotes.clear();
  for (const id of [...paxIndex.keys()]) passengers.remove(id);
  paxIndex.clear();
  carrying = null;
}

net.on('welcome', m => {
  resetWorldState();
  myId = m.id; myName = m.you.name; myCoins = m.you.coins || 0;
  srvOffset = m.srvT - performance.now();
  roster.clear();
  for (const p of m.players) {
    roster.set(p.id, { name: p.name, coins: p.coins });
    if (p.id !== myId) addRemote(p);
  }
  for (const px of m.pax) {
    paxIndex.set(px.id, stampPax(px));
    if (px.state === 'waiting') passengers.spawn(px);
    else if (px.state === 'boarded') { passengers.spawn(px); attachPaxTo(px.id, px.by); }
  }
  for (const f of m.fruits) if (f.gone) setFruitGone(f.id, true);
  if (m.rexes) for (const r of m.rexes) rexes.setState(r.id, r.state);
  if (m.stones) for (const s of m.stones) {
    const sp = stoneSpots.get(s.id);
    if (sp) { sp.gone = s.gone; sp.mesh.setEnabled(!s.gone); }
  }
  if (m.pteroDown) for (const id of m.pteroDown) setPteroDown(pteros, id, true);
  if (m.weather) weather.setRaining(m.weather === 'rain');
  if (m.level) { levelInfo = m.level; applyLevel(); }
  hud.toast(`Velkommen, <b>${myName}</b>! Du er stammens nye taxaflyver 🚁`);
});

net.on('restore', m => {
  myCoins = m.coins;
  if (m.coins > 0 || m.ture > 0) {
    hud.toast(`👋 Velkommen tilbage — 🪙 ${m.coins}, bedste stribe ${m.bestStreak}`, 4000);
  }
});

net.on('rex', m => {
  rexes.setState(m.id,
    m.ev === 'wake' ? 'waking' : m.ev === 'roar' ? 'roaring' :
    m.ev === 'stun' ? 'stunned' : 'sleeping');
  if (m.ev === 'stun') {
    hud.toast(m.by === myId
      ? '🦖💫 <b>KLONK!</b> T-rexen er lammet — fri bane i 20 sekunder!'
      : `🪨 ${roster.get(m.by)?.name || 'Nogen'} lammede en T-rex med en sten!`, 3200, 'good');
    return;
  }
  const def = REXES.find(r => r.id === m.id);
  if (!def) return;
  const d = Math.hypot(def.x - flight.pos.x, def.z - flight.pos.z);
  if (m.ev === 'wake') {
    if (audioReady()) sfxGrowl(Math.max(0, 1 - d / 220), m.in || 2000, def.x, def.z);
    if (d < 140) hud.toast('🦖 Noget stort rører på sig dernede — KOM VÆK!', 2600, 'fare');
  } else if (m.ev === 'roar') {
    const vol = Math.max(0, 1 - d / 400);
    if (vol > 0) sfxRoar(vol, def.x, def.z);
    shake(Math.max(0, 1 - d / 150));
    const R = rexBlastRFor(levelInfo.n || 1);
    if (d < R && !flight.dead) {
      // blown away: impulse away from the rex + upward, plus a bit of damage
      const f = 1 - d / R;
      const dir = new Vector3(flight.pos.x - def.x, 0, flight.pos.z - def.z).normalize();
      flight.vel.addInPlace(dir.scale((30 + 26 * f) * f)).addInPlace(new Vector3(0, 14 * f, 0));
      flight.damage += 10 + 22 * f;
      flight.landed = false;
      shake(Math.max(0.6, f));
      // the rex re-roars on a period while you linger: throttle the shouting
      const n = performance.now();
      if (n - rexToastAt > 3000) {
        rexToastAt = n;
        hud.big('🦖 <b>RAAAWR!</b> Du blev blæst afsted!', 2400);
      }
    }
  }
});

function addRemote(p) {
  if (remotes.has(p.id)) return;
  const mesh = buildHeliMesh(scene, { hue: p.hue ?? 200 });
  // name tag
  const dt = new DynamicTexture('name' + p.id, { width: 512, height: 128 }, scene, true);
  dt.hasAlpha = true;
  const c = dt.getContext();
  c.clearRect(0, 0, 512, 128);
  c.font = 'bold 64px Georgia'; c.textAlign = 'center';
  c.lineWidth = 8; c.strokeStyle = '#3d2f1f'; c.strokeText(p.name, 256, 80);
  c.fillStyle = '#ffe9b0'; c.fillText(p.name, 256, 80);
  dt.update();
  const tag = MeshBuilder.CreatePlane('tag' + p.id, { width: 6, height: 1.5 }, scene);
  const tm = new StandardMaterial('tagM' + p.id, scene);
  tm.diffuseTexture = dt; tm.useAlphaFromDiffuseTexture = true;
  tm.emissiveColor = new Color3(1, 1, 1); tm.specularColor = Color3.Black();
  tm.backFaceCulling = false;
  tag.material = tm;
  tag.billboardMode = Mesh.BILLBOARDMODE_ALL;
  tag.parent = mesh.root; tag.position.y = 5.2;
  remotes.set(p.id, { mesh, buf: new RemoteBuffer(), name: p.name, prev: null });
}

net.on('join', m => {
  if (m.p.id === myId) return; // our own join echo
  roster.set(m.p.id, { name: m.p.name, coins: m.p.coins });
  addRemote(m.p);
  hud.toast(`${m.p.name} er lettet fra sin hule 🚁`);
});
net.on('leave', m => {
  const r = remotes.get(m.id);
  if (r) { r.mesh.root.dispose(); remotes.delete(m.id); }
  dropRemoteRotor(m.id);
  const info = roster.get(m.id);
  roster.delete(m.id);
  if (info) hud.toast(`${info.name} fløj hjem for i dag`);
});

net.on('state', m => {
  if (m.srvT) srvOffset = m.srvT - performance.now();
  const at = performance.now();
  for (const p of m.ps) {
    if (p.id === myId) continue;
    const r = remotes.get(p.id);
    if (r) r.buf.push({ at, p: p.p, yaw: p.yaw, rot: p.rot, carrying: p.c });
  }
});

net.on('pax', m => {
  if (m.ev === 'spawn') {
    paxIndex.set(m.pax.id, stampPax(m.pax));
    passengers.spawn(m.pax);
    sfxUgh(m.pax.id, m.pax.cave);
    // no toast: the tower's own number slab pulses instead, so a customer in
    // the far jungle never buries "lian i rotoren"
    if (!pulseCave(m.pax.cave)) {
      hud.toast(`<b>UGH!</b> Hule ${m.pax.cave} vil til hule ${m.pax.dest} (🪙 ${m.pax.fare})`, 2600, 'info');
    }
  } else if (m.ev === 'board') {
    const px = paxIndex.get(m.id);
    if (px) { px.state = 'boarded'; px.by = m.by; }
    attachPaxTo(m.id, m.by);
    if (m.by === myId && px) {
      carrying = px;
      // a fresh trip: the last landing and the damage so far are HIS baseline
      lastVy = 99; lastVh = 99;
      dmgAtBoard = flight.damage;
      const a = CAVES.find(c => c.id === px.cave), b = CAVES.find(c => c.id === px.dest);
      const rideDist = (a && b) ? Math.hypot(a.x - b.x, a.z - b.z) : 400;
      px.rideMs = (rideDist / 12) * 1000 + 30000;
      px.deadlineAt = srvNow() * 1000 + px.rideMs;
      sfxPaxMood('board', m.id, px.cave);
      hud.big(`Kunde ombord! Flyv til hule <b>${px.dest}</b> 🪙${px.fare}`, 3000);
    }
  } else if (m.ev === 'deliver') {
    const px = paxIndex.get(m.id);
    if (m.by === myId) sfxPaxMood('deliver', m.id, px ? px.dest : null);
    passengers.dismiss(m.id, px ? px.dest : null);
    if (m.by === myId) carrying = null;
    paxIndex.delete(m.id);
  } else if (m.ev === 'left') {
    const px = paxIndex.get(m.id);
    sfxPaxMood('left', m.id, px ? px.cave : null);
    passengers.dismiss(m.id, px ? px.cave : null);
    paxIndex.delete(m.id);
    hud.toast(`😠 Kunden i hule ${px ? px.cave : '?'} blev sur og gik ind igen`, 3000, 'info');
  } else if (m.ev === 'lost') {
    // NEW: a booked customer can now run out of patience mid-flight, with no
    // crash at all — without this branch `carrying` sticks to a ghost
    if (carrying && carrying.id === m.id) {
      carrying = null;
      if (m.late) hud.big('😠 Kunden sprang af — han kom for sent!', 2800);
    }
    passengers.remove(m.id);
    paxIndex.delete(m.id);
  }
});

net.on('coins', m => {
  const r = roster.get(m.id);
  if (r) r.coins = m.coins;
  if (m.id !== myId) return;
  myCoins = m.coins;
  if (m.restore) return;                       // wallet restored at login
  if (m.bonus) { sfxCoin(3, false, 0); return; } // level bonus — fanfare covers it
  if (m.delta < 0) {                           // repair fee
    hud.big(`🔨 Reparation: 🪙 −${-m.delta}`, 2400);
    sfxFee();
    return;
  }
  if (m.delta <= 0) return;
  sfxCoin(Math.min(5, Math.ceil(m.delta / 15)), !!m.tip, m.q ?? 0);
  if (m.bounty) {
    hud.big(`🎯 Dusør: 🪙 +${m.delta}`, 2000);
  } else if (m.q !== undefined) {
    // payout = (takst + drikkepenge) x stribe, so do NOT print delta - tip as
    // the fare — it is only the fare when the streak multiplier is 1
    const mx = m.mult > 1 ? ` <b>×${m.mult}</b>` : '';
    hud.big(`🪙 +${m.delta}${mx} · drikkepenge ${m.tip}`
      + ` <small>(${Math.round(m.q * 100)} % tur)</small>`, 2600);
  } else {
    hud.big(`🪙 +${m.delta}`, 2400);
  }
  makeBurst(flight.pos, new Color4(0.95, 0.8, 0.2, 1), new Color4(1, 0.95, 0.5, 1), 30);
});

net.on('streak', m => {
  if (m.id === myId) { streak = m.streak; hud.setStreak(m.streak, m.mult); }
  else if ([6, 10, 15].includes(m.streak)) {
    hud.toast(`🔥 ${roster.get(m.id)?.name || 'Nogen'} har ${m.streak} ture i træk!`);
  }
});

net.on('fruit', m => setFruitGone(m.id, m.gone));

net.on('full', () => {
  net.full = true; // stop reconnecting
  hud.big('🚁 Junglen er FULD lige nu — prøv igen om lidt!', 0);
});

// rebuild the ptero flock when the bane calls for more of them
function applyLevel() {
  const defs = pterosForLevel(levelInfo.n);
  if (pteros && pteros.length !== defs.length) {
    for (const p of pteros) p.mesh.root.dispose();
    pteros = buildPteros(scene, defs);
    screechAt.clear();
    hud.toast('🦅 Endnu en flyveøgle er lettet — pas på deroppe!', 3000, 'fare');
  }
  hud.setLevel(levelInfo);
}

net.on('level', m => {
  levelInfo = { n: m.n, delivered: m.delivered, quota: m.quota };
  hud.setLevel(levelInfo);
});

net.on('levelup', m => {
  levelInfo = { n: m.n, delivered: 0, quota: m.quota };
  sfxFanfare();
  hud.big(`🏆 <b>BANE ${m.cleared} KLARET!</b><br><small>Stammen får 🪙 ${m.bonus} hver — junglen bliver vildere…</small>`, 4200);
  makeBurst(flight.pos, new Color4(1, 0.85, 0.3, 1), new Color4(1, 0.95, 0.6, 1), 50);
  applyLevel();
});

net.on('weather', m => {
  weather.setRaining(m.state === 'rain');
  hud.toast(m.state === 'rain'
    ? '🌧 Uvejret ruller ind — våde pedaler og LYN! Flyv lavt og forsigtigt'
    : '☀️ Solen bryder frem igen', 4000, m.state === 'rain' ? 'fare' : 'info');
});

net.on('bolt', m => {
  weather.strike(m.x, m.z);
  if (audioReady() && Math.hypot(m.x - flight.pos.x, m.z - flight.pos.z) < 60) sfxCharge();
});

function setFruitGone(id, gone) {
  const f = fruits.get(id);
  if (f) { f.gone = gone; f.mesh.setEnabled(!gone); }
}

net.on('stone', m => {
  const s = stoneSpots.get(m.id);
  if (s) { s.gone = m.gone; s.mesh.setEnabled(!m.gone); }
});

net.on('ptero', m => {
  if (m.ev === 'dive') {
    startDive(pteros, m);
    if (m.target === myId) {
      sfxScreech(0.8, m.tx, m.tz);
      hud.toast('🦅 En flyveøgle dykker mod dig!', 2400, 'fare');
    }
    return;
  }
  setPteroDown(pteros, m.id, m.ev === 'down');
  if (m.ev === 'down') {
    hud.toast(m.by === myId
      ? '🎯 <b>PLET!</b> Flyveøglen styrter til jorden!'
      : `🪨 ${roster.get(m.by)?.name || 'Nogen'} skød en flyveøgle ned!`, 3000, 'good');
  }
});

// ---------------------------------------------------------------- stones
function throwStone() {
  if (!started || !flight || flight.dead) return;
  if (!stoneCount) {
    hud.toast('🪨 Ingen sten! Flyv gennem en stenbunke først');
    return;
  }
  if (flyingStones.length >= 8) return;
  stoneCount--;
  carriedStone.setEnabled(stoneCount > 0);
  const fwd = new Vector3(Math.sin(flight.yaw), 0, Math.cos(flight.yaw));
  const mesh = MeshBuilder.CreateSphere('thrown', { diameter: 0.9, segments: 3 }, scene);
  mesh.material = stoneMatRef;
  mesh.position.copyFrom(flight.pos).addInPlace(fwd.scale(2.2));
  mesh.position.y -= 0.5;
  flyingStones.push({
    mesh,
    // 26 m/s and a lazy −14 m/s² arc: ~48 m of range, so you can actually
    // reach a rex from outside its wake radius
    vel: flight.vel.clone().addInPlace(fwd.scale(26)).addInPlace(new Vector3(0, -1, 0)),
    born: performance.now(),
  });
  sfxThrow();
}

window.addEventListener('keydown', e => {
  if (e.code === 'KeyF' && !e.repeat) throwStone();
});

function updateStones(dt) {
  for (let i = flyingStones.length - 1; i >= 0; i--) {
    const s = flyingStones[i];
    s.vel.y -= 14 * dt;
    s.mesh.position.addInPlace(s.vel.scale(dt));
    s.mesh.rotation.x += dt * 9;
    const p = s.mesh.position;
    let done = false;

    // rex hit: generous cylinder around the beast (scaled 1.35 → ~16 m tall)
    for (const def of REXES) {
      if (Math.hypot(p.x - def.x, p.z - def.z) < 9.5 && p.y > def.y - 1 && p.y < def.y + 17) {
        net.send({ t: 'hit', kind: 'rex', id: def.id });
        rexes.setState(def.id, 'stunned'); // optimistic; broadcast confirms
        sfxBonk(p.x, p.z);
        makeBurst(p, new Color4(0.7, 0.68, 0.62, 1), new Color4(0.95, 0.85, 0.4, 1), 24);
        done = true;
        break;
      }
    }
    // ptero hit
    if (!done) {
      for (const pt of pteros) {
        if (!pt.down && Vector3.Distance(pt.mesh.root.position, p) < 5) {
          net.send({ t: 'hit', kind: 'ptero', id: pt.def.id });
          setPteroDown(pteros, pt.def.id, true); // optimistic
          sfxBonk(p.x, p.z);
          makeBurst(p, new Color4(0.7, 0.55, 0.4, 1), new Color4(0.6, 0.5, 0.35, 1), 20);
          done = true;
          break;
        }
      }
    }
    // ground / water / timeout
    if (!done && p.y < WATER_Y + 0.2 && terrainHeight(p.x, p.z) < WATER_Y) {
      makeBurst(p, new Color4(0.5, 0.75, 0.85, 1), new Color4(0.7, 0.9, 1, 1), 14);
      done = true;
    } else if (!done && p.y < terrainHeight(p.x, p.z) + 0.3) {
      sfxThud(0.12, p.x, p.z);
      makeBurst(p, new Color4(0.55, 0.48, 0.35, 1), new Color4(0.7, 0.65, 0.5, 1), 12);
      done = true;
    } else if (!done && performance.now() - s.born > 6000) {
      done = true;
    }
    if (done) {
      s.mesh.dispose();
      flyingStones.splice(i, 1);
    }
  }
}

// ---------------------------------------------------------------- taxi logic
let boardTimer = 0;

function taxiUpdate(dt) {
  const pad = flight.padUnder();

  // boarding: parked on a cave pad with a waiting passenger, rotor calm, seat free
  if (pad && pad !== 'home' && !carrying && flight.rotor < 0.45) {
    const waiting = [...paxIndex.values()].find(px => px.state === 'waiting' && px.cave === pad);
    if (waiting) {
      boardTimer += dt;
      passengers.setBoardProgress(waiting.id, Math.max(0, boardTimer) / 1.1);
      if (boardTimer > 1.1) {
        boardTimer = -3; // debounce until server answers
        net.send({ t: 'board', pax: waiting.id });
      }
    } else { boardTimer = 0; passengers.clearBoardProgress(); }
  } else { boardTimer = 0; passengers.clearBoardProgress(); }

  // delivery: parked on the destination pad. The server re-checks the distance,
  // which `pad === carrying.dest` already guarantees.
  if (pad && carrying && pad === carrying.dest) {
    net.send({
      t: 'deliver', pax: carrying.id,
      vy: lastVy, vh: lastVh, dmg: Math.max(0, flight.damage - dmgAtBoard),
    });
    carrying._sent = true;
  }

  // repair + snack at home
  if (pad === 'home' && flight.rotor < 0.2) {
    if (flight.damage > 0) flight.damage = Math.max(0, flight.damage - 10 * dt);
    if (flight.energy < 100) flight.energy = Math.min(100, flight.energy + 11 * dt);
  }
}

// mission line + compass target. The energy check comes FIRST: the server keeps
// a customer waiting at all times, so an energy warning placed after the pax
// loop was simply never reached.
function missionInfo() {
  if (flight.energy < 30) {
    return { text: '🍌 Lav energi — flyv hjem til <b>H</b> og spis (eller saml frugt)', target: HOME };
  }
  if (carrying) {
    const c = CAVES.find(cc => cc.id === carrying.dest);
    return {
      text: `Aflever kunden i hule <b>${carrying.dest}</b> (🪙 ${carrying.fare})`,
      target: c, deadlineAt: carrying.deadlineAt, span: carrying.rideMs,
    };
  }
  const pad = flight.padUnder();
  const cand = [];
  for (const px of paxIndex.values()) {
    if (px.state !== 'waiting') continue;
    const c = CAVES.find(cc => cc.id === px.cave);
    if (!c) continue;
    const d = Math.hypot(c.x - flight.pos.x, c.z - flight.pos.z);
    cand.push({ px, c, score: px.fare / (1 + d / 300) });
  }
  cand.sort((a, b) => b.score - a.score);
  if (!cand.length) return { text: 'Ingen kunder lige nu — hvil på H eller saml sten', target: HOME };
  const best = cand[0];
  // standing on his pad with the blades still howling: he will not come near
  if (pad && pad === best.px.cave && flight.rotor >= 0.45) {
    return {
      text: '🌀 Sænk rotoren — han tør ikke ind under bladene',
      target: best.c, deadlineAt: best.px.deadlineAt, span: best.px.patience,
    };
  }
  const tail = cand[1] ? `<br><small>… eller hule ${cand[1].px.cave} (🪙 ${cand[1].px.fare})</small>` : '';
  const ret = best.px.retur ? '↩︎ Returtur: ' : '';
  return {
    text: `${ret}Hent kunden i hule <b>${best.px.cave}</b> → hule ${best.px.dest} (🪙 ${best.px.fare})${tail}`,
    target: best.c,
    deadlineAt: best.px.deadlineAt,
    span: best.px.patience,
  };
}

// ---------------------------------------------------------------- loop
let lastT = performance.now();
let posTimer = 0;

function loop() {
  const nowMs = performance.now();
  const dt = Math.min(0.05, (nowMs - lastT) / 1000);
  lastT = nowMs;
  const t = nowMs / 1000;
  frame++;

  if (flight.dead && respawnAt && nowMs > respawnAt) {
    // a rebuilt heli is never quite straight again
    flight._carryDmg = Math.min(30, flight.damage * 0.3);
    flight.reset();
    lastVy = 99; lastVh = 99; dmgAtBoard = 0;
    warn30 = false; warn15 = false; reserveShown = false;
    resetSurvivalAudio();
    for (const m of heli.allMeshes) m.setEnabled(true);
    carriedStone.setEnabled(false);
    respawnAt = 0;
    camSnap = true;
    hud.bigOff();
    hud.big('🔨 Ny helikopter bygget hjemme ved hulen', 2200);
  }

  const inp = started ? input(dt) : { pedal: false, throttle: 0, turn: 0 };

  if (!flight.dead) {
    flight.update(dt, started ? inp : {});
    // ONE listener update per frame, before anything can make a sound
    if (audioReady()) setListener(flight.pos.x, flight.pos.z, flight.yaw);
    taxiUpdate(dt);

    // fruit pickup
    for (const f of fruits.values()) {
      if (f.gone) continue;
      if (Vector3.Distance(f.mesh.position, flight.pos) < 4.2) {
        f.gone = true; f.mesh.setEnabled(false);
        flight.energy = Math.min(100, flight.energy + 35);
        sfxEat();
        hud.toast(`${{ banana: '🍌', melon: '🍈', meat: '🍖' }[f.spot.type]} +35 energi`, 2200, 'good');
        net.send({ t: 'fruit', id: f.spot.id });
      }
    }

    // stone pickup — up to three in the net
    if (stoneCount < 3) {
      for (const s of stoneSpots.values()) {
        if (s.gone) continue;
        if (Vector3.Distance(s.mesh.position, flight.pos) < 4.5) {
          s.gone = true; s.mesh.setEnabled(false);
          stoneCount++;
          carriedStone.setEnabled(true);
          sfxPickupStone();
          hud.toast(touch.active
            ? `🪨 Sten samlet op (${stoneCount}/3) — tryk på <b>🪨-knappen</b> for at kaste!`
            : `🪨 Sten samlet op (${stoneCount}/3) — tryk <b>F</b> for at kaste!`, 2600, 'good');
          net.send({ t: 'stone', id: s.spot.id });
          break;
        }
      }
    }

    // pterodactyl collision. updatePteros returns {v, at, diving}; a bird that
    // is mid-dive hits harder and has a slightly fatter hitbox.
    const hit = updatePteros(pteros, srvNow(), flight.pos, t, dt);
    if (hit && hit.v) {
      const diving = !!hit.diving;
      flight.vel.addInPlace(hit.v);
      flight.damage += diving ? 18 : 8;
      flight.landed = false;
      shake(diving ? 0.9 : 0.5);
      sfxSquawk(hit.at.x, hit.at.z);
      hud.toast('🦅 SKRIIIG! En flyveøgle ramte dig!', 2600, 'fare');
      if (diving && (levelInfo.n || 1) >= 4 && carrying && Math.random() < 0.4) {
        net.send({ t: 'crash', pax: carrying.id });
        passengers.remove(carrying.id);
        paxIndex.delete(carrying.id);
        carrying = null;
        hud.big('🦅 Flyveøglen snuppede kunden!', 2600);
      }
    }
  } else {
    updatePteros(pteros, srvNow(), null, t, dt);
  }

  updateStones(dt);
  hud.setStone(stoneCount);
  touch.setStones(stoneCount);

  flight.wet = weather.wet;
  flight.loaded = carrying ? 1 : 0;
  // storm gusts: slow rolling push that gets serious when soaked
  const gust = weather.wet * 4.2;
  flight.windX = Math.sin(t * 0.31) * gust + Math.sin(t * 1.13) * gust * 0.4;
  flight.windZ = Math.cos(t * 0.27) * gust + Math.cos(t * 0.97) * gust * 0.4;
  weather.update(t, dt, flight.dead ? null : flight.pos);
  if (audioReady()) setRainAudio(weather.wet);
  // the sun shafts fade away as the storm rolls in — and the cone nearest the
  // camera dims out, so you never fly into a wall of beige
  const dry = 1 - weather.wet;
  for (const sh of world.shafts) {
    const d = Math.hypot(camera.position.x - sh.x, camera.position.z - sh.z);
    const near = Math.max(0, Math.min(1, (d - sh.fadeR * 1.5) / (sh.fadeR * 6)));
    sh.sm.alpha = 0.055 * dry * near;
    sh.pm.alpha = 0.13 * dry;
  }
  if (world.rayMat) world.rayMat.alpha = 0.055 * (1 - weather.wet);
  // pollen drifts around the player (washed out of the air by rain)
  world.motes.emitter.copyFrom(flight.pos);
  world.motes.emitRate = 16 * (1 - weather.wet);

  // number-slab pulses (a customer just shouted from that tower)
  for (const [id, s] of slabPulse) {
    if (nowMs > s.until) { s.mat.emissiveColor.copyFrom(s.base); slabPulse.delete(id); continue; }
    const k = 0.5 + 0.5 * Math.sin(t * 9);
    s.mat.emissiveColor.copyFrom(Color3.Lerp(s.base, PULSE_COL, 0.35 + 0.45 * k));
  }

  applyHeliVisual(heli, flight, t, dt);

  // --- audio: rotor, room, water, survival. Cavelon keeps pedalling on the
  // reserve, so the energy gate is gone from the rotor call.
  setRotor(audioReady() ? flight.rotor : 0, inp.pedal && !flight.dead,
    { vy: flight.vel.y, wet: weather.wet, dt });
  if (audioReady()) {
    setEnergyState({
      energy: flight.energy, pedaling: inp.pedal,
      sputter: flight.sputter, reserve: flight.reserve,
    });
    setHullStress(flight.damage);
    if (frame % 10 === 0) {
      const ceil = ceilingY(flight.pos.x, flight.pos.z);
      setRoom(Math.max(0, Math.min(1, flight.pos.y / ceil)), weather.wet, flight.inCrown);
      const dRiver = Math.abs(flight.pos.x - riverX(flight.pos.z));
      setWaterAudio(dRiver, flight.pos.y - WATER_Y);
    }
    if (!flight.dead && flight.padUnder() && flight.rotor < 0.1) sfxIdleTick();
  }

  // energy warnings — one-shot flags that re-arm above 40
  if (!flight.dead) {
    if (flight.reserve && !reserveShown) {
      reserveShown = true;
      hud.big('⚠️ RESERVE — Cavelon træder på det sidste!', 0);
    } else if (!flight.reserve && reserveShown) {
      reserveShown = false;
      hud.bigOff();
    }
    if (flight.energy > 40) {
      warn30 = false; warn15 = false;
    } else if (flight.energy < 15 && !warn15) {
      warn15 = true; hud.toast('🥵 Energien er næsten væk!', 3200, 'fare');
    } else if (flight.energy < 30 && !warn30) {
      warn30 = true; hud.toast('🍖 Energi under 30 — find frugt eller flyv hjem', 3200, 'fare');
    }
  }

  // fruit bobbing
  for (const f of fruits.values()) {
    if (f.gone) continue;
    f.mesh.position.y = f.spot.y + Math.sin(t * 1.8 + f.spot.id) * 0.7;
    f.mesh.rotation.y = t * 0.9 + f.spot.id;
  }

  // remotes — applyRemoteVisual needs two samples to derive bank and cadence
  const renderT = nowMs - 120;
  for (const [id, r] of remotes) {
    const s = r.buf.sample(renderT);
    if (!s) continue;
    if (!r.prev) { r.prev = s; continue; }
    applyRemoteVisual(r.mesh, s, r.prev, t, dt);
    r.prev = s;
    if (audioReady()) remoteRotor(id, s.p[0], s.p[2], s.rot || 0);
  }

  passengers.update(t, dt, srvNow() * 1000);
  rexes.update(t, dt, flight.dead ? null : flight.pos, levelInfo.n || 1);

  // faint snoring when drifting near a sleeping rex — the only warning you get
  if (audioReady() && nowMs > snoreAt) {
    for (const r of rexes.list) {
      if (r.state !== 'sleeping') continue;
      const d = Math.hypot(r.def.x - flight.pos.x, r.def.z - flight.pos.z);
      if (d < 110) {
        sfxSnore(0.2 * (1 - d / 110), r.def.x, r.def.z);
        snoreAt = nowMs + 2400;
        break;
      }
    }
  }

  // a screech per approaching bird, throttled PER BIRD
  if (audioReady() && !flight.dead) {
    for (const p of pteros) {
      if (p.down) continue;
      const bp = p.mesh.root.position;
      const d = Math.hypot(bp.x - flight.pos.x, bp.z - flight.pos.z);
      if (d > 60) continue;
      if (nowMs < (screechAt.get(p.def.id) || 0)) continue;
      screechAt.set(p.def.id, nowMs + 2500);
      sfxScreech(0.30 * (1 - d / 60), bp.x, bp.z);
    }
  }

  // ground fx: downwash under the rotor, fading landing ring
  updateGroundFx(t, dt);

  // network position updates ~15 Hz
  posTimer += dt;
  if (net.connected && posTimer > 1 / 15) {
    posTimer = 0;
    net.send({
      t: 'pos',
      p: [+flight.pos.x.toFixed(2), +flight.pos.y.toFixed(2), +flight.pos.z.toFixed(2)],
      yaw: +flight.yaw.toFixed(3),
      rot: +flight.rotor.toFixed(2),
      c: carrying ? 1 : 0,
      coins: myCoins,
    });
  }

  // ---- everything below is chase-camera and colour grade. tools/ drive the
  // camera themselves, so photo mode returns BEFORE any of it.
  const fwd = new Vector3(Math.sin(flight.yaw), 0, Math.cos(flight.yaw));
  if (window.__photoCam) { scene.render(); return; }

  const spd = Math.hypot(flight.vel.x, flight.vel.z);
  camShake *= Math.exp(-2.6 * dt);

  // field of view opens up with speed and with a jolt
  const fovTarget = 0.80 + 0.10 * Math.min(1, spd / 28) + camShake * 0.06;
  camera.fov += (fovTarget - camera.fov) * Math.min(1, dt * 3);

  // chase distance grows with speed, then gets cut short by anything solid
  const far = (keys.KeyC || touch.camFar) ? 22 : 14;
  let dist = far + 5 * Math.min(1, spd / 26);
  const dirX = -fwd.x, dirZ = -fwd.z;      // heli → camera
  for (const tr of GIANT_TREES) {
    const rx = tr.x - flight.pos.x, rz = tr.z - flight.pos.z;
    const R = tr.trunkR + 2.5;
    if (Math.abs(rx) > dist + R || Math.abs(rz) > dist + R) continue;   // AABB reject
    const proj = rx * dirX + rz * dirZ;
    if (proj < 1 || proj > dist + R) continue;
    const perp = Math.abs(rx * dirZ - rz * dirX);
    if (perp > R) continue;
    dist = Math.min(dist, Math.max(6, proj - Math.sqrt(R * R - perp * perp) - 0.5));
  }
  const wanted = flight.pos.subtract(fwd.scale(dist)).add(new Vector3(0, dist * 0.42, 0));
  const camGround = terrainHeight(wanted.x, wanted.z) + 2;
  if (wanted.y < camGround) wanted.y = camGround;
  const camCeil = ceilingY(wanted.x, wanted.z) - 2;
  if (wanted.y > camCeil) wanted.y = camCeil;

  if (camSnap) { camera.position.copyFrom(wanted); camSnap = false; }
  else {
    // split axes: horizontal follows tight, vertical floats
    const kxz = Math.min(1, dt * 5.5), ky = Math.min(1, dt * 2.2);
    camera.position.x += (wanted.x - camera.position.x) * kxz;
    camera.position.z += (wanted.z - camera.position.z) * kxz;
    camera.position.y += (wanted.y - camera.position.y) * ky;
  }
  if (camShake > 0.005) {
    camera.position.addInPlace(new Vector3(
      (Math.random() - 0.5) * camShake * 2.6,
      (Math.random() - 0.5) * camShake * 2.0,
      (Math.random() - 0.5) * camShake * 2.6,
    ));
  }
  // aim where he is GOING, not where he is
  camera.setTarget(flight.pos
    .add(fwd.scale(6))
    .add(new Vector3(flight.vel.x * 0.25, 2 + flight.vel.y * 0.12, flight.vel.z * 0.25)));

  // --- colour grade (main.js is the only writer)
  const dd = Math.max(0, flight.damage - prevDamage);
  prevDamage = flight.damage;
  hurt += dd / 18;
  hurt *= Math.exp(-3.2 * dt);
  grade.vignetteWeight = (1.1 - 0.35 * weather.wet) + 4.5 * hurt + 1.2 * (flight.damage / 100)
    + (flight.damage > 70 ? Math.sin(t * 5.7) * 0.5 : 0);
  const vk = Math.max(hurt, flight.damage / 100 * 0.7);
  grade.vignetteColor = Color4.Lerp(VIG_CALM, VIG_HURT, Math.min(1, vk));
  grade.exposure = 1.30 + exposurePunch;
  exposurePunch *= Math.exp(-8 * dt);

  // ---- HUD
  const mi = missionInfo();
  let frac = null;
  if (mi.deadlineAt && mi.span) {
    frac = Math.max(0, Math.min(1, (mi.deadlineAt - srvNow() * 1000) / mi.span));
  }
  hud.setMission(mi.text, frac);
  if (mi.target) {
    const dirWorld = Math.atan2(mi.target.x - flight.pos.x, mi.target.z - flight.pos.z);
    // compass arrow relative to camera view direction
    const camYaw = Math.atan2(camera.getForwardRay().direction.x, camera.getForwardRay().direction.z);
    hud.setCompass(-(dirWorld - camYaw), true);
  } else hud.setCompass(0, false);

  const hb = hoverRotor(spd, flight.wet, flight.loaded);
  const sink = Math.max(0, -flight.vel.y);
  hud.update({
    energy: flight.energy,
    damage: flight.damage,
    coins: myCoins,
    speed: spd,
    alt: flight.pos.y - Math.max(terrainHeight(flight.pos.x, flight.pos.z), WATER_Y),
    rotor: flight.rotor,
    hoverBand: [hb - 0.04, hb + 0.04],
    sink,
    landQ: landScore(sink, spd),
  });

  const rows = [...roster.entries()]
    .map(([id, r]) => ({ name: r.name, coins: r.coins || 0, you: id === myId }))
    .sort((a, b) => b.coins - a.coins);
  hud.showBoard(rows, !!(keys.Tab || touch.board));

  scene.render();
}

// The two shared ground effects: never rebuilt, only moved and faded.
function updateGroundFx(t, dt) {
  if (landRingT > 0) {
    landRingT += dt;
    const k = landRingT / 0.7;
    if (k >= 1) { landRingT = 0; landRing.setEnabled(false); landRingMat.alpha = 0; }
    else {
      landRing.scaling.setAll(0.5 + k * 1.6);
      landRingMat.alpha = 0.55 * (1 - k);
    }
  }
  if (!downwash) return;
  const g = Math.max(terrainHeight(flight.pos.x, flight.pos.z), WATER_Y);
  const agl = flight.pos.y - g;
  const a = flight.dead ? 0 : 0.34 * Math.min(1, flight.rotor / 0.5) * Math.max(0, 1 - agl / 16);
  if (a < 0.01) { if (downwash.isEnabled()) downwash.setEnabled(false); return; }
  downwash.setEnabled(true);
  downwash.position.set(flight.pos.x, g + 0.3, flight.pos.z);
  downwash.scaling.setAll(0.7 + agl / 16 * 0.9);
  downwash.rotation.y = t * 1.4;
  downwashMat.alpha = a;
}

boot();

// test hook (browser smoke tests drive this)
window.__ugh = {
  get flight() { return flight; }, get net() { return net; }, get remotes() { return remotes; },
  get paxIndex() { return paxIndex; }, get carrying() { return carrying; },
  get rexes() { return rexes; }, get pteros() { return pteros; },
  get weather() { return weather; }, get levelInfo() { return levelInfo; },
  get touch() { return touch; }, input,
  get rotor() { return flight ? flight.rotor : 0; },
  get stoneCount() { return stoneCount; },
  get hasStone() { return stoneCount > 0; },
  get streak() { return streak; },
  get stoneSpots() { return stoneSpots; },
  get flyingStones() { return flyingStones; },
  throwStone,
  get myId() { return myId; }, get coins() { return myCoins; }, get fruits() { return fruits; },
  scene, engine, camera, grade,
};
